import type { Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { queryKeys } from '~/queryClient'
import financialBff from '~/services-v2/financial-bff/financial-bff'

export default function useCustomerBiometry(projectId: Ref<string>, enabled: Ref<boolean>) {
  const queryKey = computed(() => queryKeys.CONTRACT_BIOMETRY.DATA(projectId.value))

  const queryFn = async () => {
    if (!enabled.value) {
      return null
    }

    const res = await financialBff('query')({
      formalization: [{ projectId: projectId.value }, {
        resend: true,
        cancel: true,
        status: true,
        status_reason: {
          code: true,
          description: true,
        },
        cancellation_type: true,
        download: {
          contract: true,
          insurance: true,
          term: true,
        },
        pendencies: {
          expired_at: true,
          items: {
            category: true,
            message: true,
          },
        },
        send_biometry_contract: {
          message: {
            days: true,
            time: true,
          },
          method_receipt: true,
        },
        biometry: {
          pending: true,
          status: true,
          signers: {
            name: true,
            checked: true,
          },
        },
        contract: {
          status: true,
          has_biometric: true,
          signers: {
            name: true,
            checked: true,
          },
        },
        term: {
          status: true,
          signers: {
            name: true,
            checked: true,
          },
        },
        contracts: {
          financing_amendment_term: {
            status: true,
            document_signed: true,
            signers: {
              name: true,
              checked: true,
            },
          },
          loan: {
            status: true,
            document_signed: true,
            signers: {
              name: true,
              checked: true,
            },
          },
          lender_insurance: {
            status: true,
            document_signed: true,
            signers: {
              name: true,
              checked: true,
            },
          },
          diverse_risks_insurance: {
            status: true,
            document_signed: true,
            signers: {
              name: true,
              checked: true,
            },
          },
          combo_connect_term: {
            status: true,
            document_signed: true,
            signers: {
              name: true,
              checked: true,
            },
          },
        },
        approval: true,
        remaining_contract_days_until_expiration: true,
        order_id: true,
      }],
    })

    return res.formalization
  }

  return useQuery({
    queryFn,
    queryKey: queryKey.value,
    retry: 3,
    retryDelay: 20 * 1000, // 20s
    staleTime: 10 * 1000, // 10 seconds
    refetchInterval: 10 * 1000, // 10 seconds
    enabled: () => enabled.value,
  })
}

export type UseCustomerBiometryData = ReturnType<typeof useCustomerBiometry>['data']['value']
